/**
 * The authoritative game state, and the two functions that build and copy it.
 *
 * `engine/` is a pure reducer: no sockets, no fs, no `Date.now()`. Everything
 * time-related arrives as a tick index; everything tunable arrives as content.
 */

import type {
  Cell,
  Container,
  GameEvent,
  Order,
  PendingPlate,
  Player,
  PlayerId,
  ScheduledOrder,
  SerialisedState,
  Station,
  StationType,
} from '@overcooked/shared'
import { cellKey, isCookingEquipment, msToTicks } from '@overcooked/shared'
import type { Content, MapContent } from './content'
import { mapNamed, orderPoolFor } from './content'
import { cloneContainer, cloneItem } from './cloneItem'
import { equipmentForRack } from './rules'
import { inheritStationOrder } from './stationOrder'

export interface GameState {
  tick: number
  mapName: string
  width: number
  height: number
  /**
   * Every cell of the grid, keyed by `cellKey` — walls and floor included, so
   * a keyframe alone describes the whole kitchen ("the log alone is
   * sufficient to reconstruct the round").
   */
  stations: Record<string, Station>
  /** Never `{ A, B }`. */
  players: Record<PlayerId, Player>
  orders: Order[]
  /** Served plates on their way back to the plate return. */
  pendingPlates: PendingPlate[]
  score: number
  roundDurationTicks: number
  /**
   * Which dishes this round's map can be asked for. Carried in state rather
   * than looked up so a stored log from one map replays correctly against a
   * server that now serves another.
   */
  orderPool: string[]
  /**
   * The map's `// schedule:`, when it has one: which tickets arrive when.
   * Absent means the cadence over `orderPool` — every map without a schedule,
   * and every keyframe recorded before schedules existed.
   */
  orderSchedule?: ScheduledOrder[]
}

const WALKABLE: ReadonlySet<StationType> = new Set<StationType>(['floor'])

export function isWalkable(state: GameState, cell: Cell): boolean {
  const station = state.stations[cellKey(cell)]
  return station !== undefined && WALKABLE.has(station.type)
}

export function stationAt(state: GameState, cell: Cell): Station | undefined {
  return state.stations[cellKey(cell)]
}

/** Deterministic iteration order for anything that resolves per player. */
export function playersBySeat(state: GameState): Player[] {
  return Object.values(state.players).sort((a, b) => a.seat - b.seat)
}

export function initialState(
  content: Content,
  playerIds: PlayerId[],
  mapName?: string | null,
): GameState {
  const { timings } = content
  const map: MapContent = mapName ? mapNamed(content, mapName) : content.map

  if (playerIds.length > map.spawns.length) {
    throw new Error(
      `map "${map.name}" has ${map.spawns.length} spawns but ${playerIds.length} players were seated`,
    )
  }

  const stations: Record<string, Station> = {}
  let plateStacksSeen = 0

  for (const tile of Object.values(map.tiles)) {
    const isFirstPlateStack = tile.type === 'plate_stack' && plateStacksSeen++ === 0
    // A rack starts holding the thing it is a rack for. That is the whole of
    // "the extinguisher lives somewhere on the map to be picked up" — one
    // glyph, and the ordinary pick-up rule does the rest.
    const rackItem = equipmentForRack(content, tile.type)
    stations[cellKey(tile.cell)] = {
      type: tile.type,
      cell: { ...tile.cell },
      item: rackItem ? { type: 'equipment', id: rackItem } : null,
      progressTicks: 0,
      progressTargetTicks: 0,
      resumedAtTick: null,
      heldBy: null,
      heldSubject: null,
      container: null,
      onFire: false,
      dispenses: tile.dispenses,
      cleanPlates: isFirstPlateStack ? timings.platesPerMap : 0,
      // Every plate starts clean and on the stack, so every return starts empty.
      dirtyPlates: 0,
    }
  }

  // Containers the layout starts with. Ids are derived from the placement, so
  // they are identical on every run and on every replay — a container is
  // followed through the round the same way a plate is.
  map.containers.forEach((placement, index) => {
    const station = stations[cellKey(placement.cell)]
    if (!station) return
    const container: Container = {
      id: `${placement.kind}-${index}-${placement.cell.x}-${placement.cell.y}`,
      kind: placement.kind,
      contents: [],
      cookState: 'empty',
      phaseStartedAtTick: null,
      phaseEndsAtTick: null,
    }
    if (isCookingEquipment(station.type)) station.container = container
    else station.item = { type: 'container', container }
  })

  const players: Record<PlayerId, Player> = {}
  playerIds.forEach((id, index) => {
    players[id] = {
      id,
      seat: index + 1,
      cell: { ...map.spawns[index] },
      facing: 'down',
      holding: null,
      connected: true,
    }
  })

  return {
    tick: 0,
    mapName: map.name,
    width: map.width,
    height: map.height,
    stations,
    players,
    orders: [],
    pendingPlates: [],
    score: 0,
    roundDurationTicks: msToTicks(timings.roundLengthMs),
    orderPool: orderPoolFor(content, map),
    ...withSchedule(map.orderSchedule),
  }
}

/** `{ orderSchedule }` copied when there is one, `{}` when not — never an empty list. */
function withSchedule(schedule: readonly ScheduledOrder[] | undefined): { orderSchedule?: ScheduledOrder[] } {
  return schedule && schedule.length > 0 ? { orderSchedule: schedule.map((e) => ({ ...e })) } : {}
}

export function cloneState(state: GameState): GameState {
  const stations: Record<string, Station> = {}
  let stationCount = 0
  for (const [key, s] of Object.entries(state.stations)) {
    stationCount += 1
    stations[key] = {
      type: s.type,
      cell: { x: s.cell.x, y: s.cell.y },
      item: s.item === null ? null : cloneItem(s.item),
      progressTicks: s.progressTicks,
      progressTargetTicks: s.progressTargetTicks,
      resumedAtTick: s.resumedAtTick,
      heldBy: s.heldBy,
      heldSubject: s.heldSubject ?? null,
      container: s.container === null ? null : cloneContainer(s.container),
      onFire: s.onFire,
      dispenses: s.dispenses,
      cleanPlates: s.cleanPlates,
      dirtyPlates: s.dirtyPlates,
    }
  }
  inheritStationOrder(state.stations, stations, stationCount)

  const players: Record<PlayerId, Player> = {}
  for (const [id, p] of Object.entries(state.players)) {
    players[id] = {
      id: p.id,
      seat: p.seat,
      cell: { x: p.cell.x, y: p.cell.y },
      facing: p.facing,
      holding: p.holding === null ? null : cloneItem(p.holding),
      connected: p.connected,
    }
  }

  return {
    tick: state.tick,
    mapName: state.mapName,
    width: state.width,
    height: state.height,
    stations,
    players,
    orders: state.orders.map((o) => ({ ...o })),
    pendingPlates: state.pendingPlates.map((p) => ({ ...p })),
    score: state.score,
    roundDurationTicks: state.roundDurationTicks,
    orderPool: [...state.orderPool],
    ...withSchedule(state.orderSchedule),
  }
}

/** The keyframe payload, and what the client renders from. */
export function serialise(state: GameState): SerialisedState {
  return {
    tick: state.tick,
    mapName: state.mapName,
    players: playersBySeat(state).map((p) => structuredClone(p)),
    stations: Object.values(state.stations).map((s) => structuredClone(s)),
    orders: state.orders.map((o) => ({ ...o })),
    pendingPlates: state.pendingPlates.map((p) => ({ ...p })),
    score: state.score,
    roundDurationTicks: state.roundDurationTicks,
    orderPool: [...state.orderPool],
    ...withSchedule(state.orderSchedule),
  }
}

export function deserialise(snapshot: SerialisedState, width: number, height: number): GameState {
  const stations: Record<string, Station> = {}
  for (const s of snapshot.stations) {
    const station = structuredClone(s)
    // A keyframe recorded before the plate return kept a count has no field to
    // clone. Zero is what it meant: the return held its one plate in `item`.
    station.dirtyPlates = station.dirtyPlates ?? 0
    // Likewise for a keyframe recorded before a wash was tied to its plate:
    // "no wash in progress" is what the absence meant.
    station.heldSubject = station.heldSubject ?? null
    stations[cellKey(s.cell)] = station
  }

  const players: Record<PlayerId, Player> = {}
  for (const p of snapshot.players) players[p.id] = structuredClone(p)

  return {
    tick: snapshot.tick,
    mapName: snapshot.mapName,
    width,
    height,
    stations,
    players,
    orders: snapshot.orders.map((o) => ({ ...o })),
    pendingPlates: snapshot.pendingPlates.map((p) => ({ ...p })),
    score: snapshot.score,
    roundDurationTicks: snapshot.roundDurationTicks,
    orderPool: [...(snapshot.orderPool ?? [])],
    // A keyframe recorded before schedules existed has none, and ran the cadence.
    ...withSchedule(snapshot.orderSchedule),
  }
}

/**
 * Engine events carry `wallClock: 0`; the transport stamps the real value at
 * emit time. Wall-clock exists purely to align the log against the audio
 *, so keeping it out of `engine/` keeps the reducer pure.
 */
export function stampWallClock(events: GameEvent[], wallClock: number): GameEvent[] {
  return events.map((e) => ({ ...e, wallClock }))
}
